import * as React from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { getAssetString } from 'helpers/assets';
import { getSorobanTokenMetadata } from 'helpers/soroban-token-metadata';
import { createAsset } from 'helpers/token';

import { LumenInfo } from 'store/assetsStore/reducer';
import useAssetsStore from 'store/assetsStore/useAssetsStore';

import { ClassicToken, Token, TokenType } from 'types/token';

import SuccessGreen from 'assets/icons/status/success-green-16.svg';
import UnknownLogo from 'assets/tokens/asset-unknown-logo.svg';
import SorobanLogo from 'assets/tokens/soroban-token-logo.svg';

import { flexAllCenter } from 'styles/mixins';
import { COLORS } from 'styles/style-constants';

import { CircleLoader } from './loaders';

export const logoStyles = (isCircle = true) => css`
    height: 3.2rem;
    width: 3.2rem;
    max-height: 3.2rem;
    max-width: 3.2rem;
    min-width: 3.2rem;
    border-radius: ${isCircle ? '50%' : '0.1rem'};
`;

const smallLogoStyles = (isCircle: boolean) => css`
    height: 1.6rem;
    width: 1.6rem;
    max-height: 1.6rem;
    max-width: 1.6rem;
    min-width: 1.6rem;
    border-radius: ${isCircle ? '50%' : '0.1rem'};
`;

export const bigLogoStyles = (isCircle: boolean) => css`
    height: 8rem;
    width: 8rem;
    max-height: 8rem;
    max-width: 8rem;
    min-width: 8rem;
    border-radius: ${isCircle ? '50%' : '0.5rem'};
`;

export const customLogoSize = (size: number, isCircle = true) => css`
    height: ${size}rem;
    width: ${size}rem;
    max-height: ${size}rem;
    max-width: ${size}rem;
    min-width: ${size}rem;
    border-radius: ${isCircle ? '50%' : '0.5rem'};
`;

const Logo = styled.img<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $isCircle?: boolean;
    $size?: number;
}>`
    ${({ $isSmall, $isBig, $isCircle, $size }) => {
        if ($size) {
            return customLogoSize($size, $isCircle);
        }
        if ($isSmall) {
            return smallLogoStyles($isCircle);
        }
        if ($isBig) {
            return bigLogoStyles($isCircle);
        }
        return logoStyles($isCircle);
    }}
`;

const Unknown = styled(UnknownLogo)<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $isCircle?: boolean;
    $size?: number;
}>`
    ${({ $isSmall, $isBig, $isCircle, $size }) => {
        if ($size) {
            return customLogoSize($size, $isCircle);
        }
        if ($isSmall) {
            return smallLogoStyles($isCircle);
        }
        if ($isBig) {
            return bigLogoStyles($isCircle);
        }
        return logoStyles($isCircle);
    }}
`;

const Soroban = styled(SorobanLogo)<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $isCircle?: boolean;
    $size?: number;
}>`
    ${({ $isSmall, $isBig, $isCircle, $size }) => {
        if ($size) {
            return customLogoSize($size, $isCircle);
        }
        if ($isSmall) {
            return smallLogoStyles($isCircle);
        }
        if ($isBig) {
            return bigLogoStyles($isCircle);
        }
        return logoStyles($isCircle);
    }}
`;

const LogoLoaderContainer = styled.div<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $isCircle?: boolean;
    $size?: number;
}>`
    ${({ $isSmall, $isBig, $isCircle, $size }) => {
        if ($size) {
            return customLogoSize($size, $isCircle);
        }
        if ($isSmall) {
            return smallLogoStyles($isCircle);
        }
        if ($isBig) {
            return bigLogoStyles($isCircle);
        }
        return logoStyles($isCircle);
    }}
    ${flexAllCenter};
    background-color: ${COLORS.textSecondary};
`;

const LogoLoader = styled(CircleLoader)`
    color: ${COLORS.white};
`;

const WhitelistedLogo = styled.span<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $isCircle?: boolean;
    $size?: number;
}>`
    ${({ $isSmall, $isBig, $isCircle, $size }) => {
        if ($size) {
            return customLogoSize($size, $isCircle);
        }
        if ($isSmall) {
            return smallLogoStyles($isCircle);
        }
        if ($isBig) {
            return bigLogoStyles($isCircle);
        }
        return logoStyles($isCircle);
    }}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: visible;

    &::before {
        content: '';
        position: absolute;
        width: ${({ $isSmall, $isBig, $size }) =>
            $isBig ? '9rem' : $isSmall || ($size && $size <= 1.6) ? '2rem' : '3.8rem'};
        height: ${({ $isSmall, $isBig, $size }) =>
            $isBig ? '9rem' : $isSmall || ($size && $size <= 1.6) ? '1.7rem' : '3.8rem'};
        left: 50%;
        bottom: ${({ $isSmall, $isBig, $size }) =>
            $isBig ? '-0.6rem' : $isSmall || ($size && $size <= 1.6) ? '-0.1rem' : '-0.3rem'};
        transform: translateX(-50%);
        border-radius: 50%;
        background: linear-gradient(0deg, ${COLORS.green50} 0%, rgba(204, 250, 223, 0) 60.526%);
        pointer-events: none;
    }
`;

const LogoContent = styled.span`
    display: inline-flex;
    position: relative;
    z-index: 1;
`;

const WhitelistCheck = styled.span<{
    $isSmall?: boolean;
    $isBig?: boolean;
    $size?: number;
}>`
    position: absolute;
    z-index: 2;
    left: 50%;
    bottom: ${({ $isSmall, $isBig, $size }) =>
        $isBig ? '-1.2rem' : $isSmall || ($size && $size <= 1.6) ? '-0.35rem' : '-0.9rem'};
    width: ${({ $isSmall, $size }) => ($isSmall || ($size && $size <= 1.6) ? '1.2rem' : '1.6rem')};
    height: ${({ $isSmall, $size }) => ($isSmall || ($size && $size <= 1.6) ? '1.2rem' : '1.6rem')};
    transform: translateX(-50%);

    svg {
        display: block;
        width: 100%;
        height: 100%;
    }
`;

type AssetLogoProps = {
    asset: Token;
    isSmall?: boolean;
    isBig?: boolean;
    isCircle?: boolean;
    isWhitelisted?: boolean;
    showWhitelistedIcon?: boolean;
    size?: number;
    className?: string;
    style?: React.CSSProperties;
};

const AssetLogo = ({
    asset,
    isSmall,
    isBig,
    isCircle,
    isWhitelisted,
    showWhitelistedIcon,
    size,
    className,
    style,
}: AssetLogoProps) => {
    const [isErrorLoad, setIsErrorLoad] = useState(false);

    const { assetsInfo } = useAssetsStore();
    const sorobanLogo =
        asset.type === TokenType.soroban ? getSorobanTokenMetadata(asset).logo : null;

    useEffect(() => {
        setIsErrorLoad(false);
    }, [asset.contract, sorobanLogo]);

    const wrapIfWhitelisted = (logo: React.ReactNode) => {
        if (!isWhitelisted) {
            return logo;
        }

        return (
            <WhitelistedLogo
                $isSmall={isSmall}
                $isBig={isBig}
                $isCircle={isCircle}
                $size={size}
                className={className}
                style={style}
            >
                <LogoContent>{logo}</LogoContent>
                {showWhitelistedIcon && (
                    <WhitelistCheck $isSmall={isSmall} $isBig={isBig} $size={size}>
                        <SuccessGreen />
                    </WhitelistCheck>
                )}
            </WhitelistedLogo>
        );
    };

    if (!asset.type && !(asset as ClassicToken).issuer) {
        return wrapIfWhitelisted(
            <Unknown
                $isSmall={isSmall}
                $isBig={isBig}
                $isCircle={isCircle}
                $size={size}
                className={isWhitelisted ? undefined : className}
                style={isWhitelisted ? undefined : style}
            />,
        );
    }

    if (asset.type === TokenType.soroban) {
        if (sorobanLogo && !isErrorLoad) {
            return wrapIfWhitelisted(
                <Logo
                    src={sorobanLogo}
                    alt=""
                    $isSmall={isSmall}
                    $isBig={isBig}
                    $isCircle={isCircle}
                    $size={size}
                    onError={() => {
                        setIsErrorLoad(true);
                    }}
                    className={isWhitelisted ? undefined : className}
                    style={isWhitelisted ? undefined : style}
                />,
            );
        }

        return wrapIfWhitelisted(
            <Soroban
                $isSmall={isSmall}
                $isBig={isBig}
                $isCircle={isCircle}
                $size={size}
                className={isWhitelisted ? undefined : className}
                style={isWhitelisted ? undefined : style}
            />,
        );
    }

    const assetInstance = createAsset(asset.code, asset.issuer);
    const isNative = assetInstance.isNative();
    const assetInfo = isNative
        ? LumenInfo
        : assetsInfo.get(getAssetString(assetInstance as ClassicToken));
    const logoUrl = assetInfo?.image;

    if (logoUrl === undefined) {
        return wrapIfWhitelisted(
            <LogoLoaderContainer
                $isSmall={isSmall}
                $isBig={isBig}
                $isCircle={isCircle}
                $size={size}
                className={isWhitelisted ? undefined : className}
                style={isWhitelisted ? undefined : style}
            >
                <LogoLoader size="small" />
            </LogoLoaderContainer>,
        );
    }

    if (logoUrl === null || isErrorLoad) {
        return wrapIfWhitelisted(
            <Unknown
                $isSmall={isSmall}
                $isBig={isBig}
                $isCircle={isCircle}
                $size={size}
                className={isWhitelisted ? undefined : className}
                style={isWhitelisted ? undefined : style}
            />,
        );
    }

    return wrapIfWhitelisted(
        <Logo
            src={logoUrl}
            alt=""
            $isSmall={isSmall}
            $isBig={isBig}
            $isCircle={isCircle}
            $size={size}
            onError={() => {
                setIsErrorLoad(true);
            }}
            className={isWhitelisted ? undefined : className}
            style={isWhitelisted ? undefined : style}
        />,
    );
};

export default AssetLogo;
